import { Box, Chip, IconButton, Tooltip, Typography } from '@material-ui/core'
import { Close, HelpOutline } from '@material-ui/icons'
import { AutocompleteChangeReason } from '@material-ui/lab/Autocomplete'
import { Fragment } from 'react'
import { Controller } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { Autocomplete } from '@/components/auto-complete'
import Button from '@/components/button'
import InputText from '@/components/input-text'
import { useGetAddressCities } from '@/hooks/queries/use-get-address-cities'
import { useGetAddressStates } from '@/hooks/queries/use-get-address-states'
import { useProject } from '@/pages/projects-form/contexts/project-context'
import { City, State } from '@/services/project-adress'
import { Region } from '@/types/projects'

import { useStyles } from './styles'

interface RegionDTO extends Omit<Region, 'id'> {
  id?: Region['id']
  removed?: boolean
  updated?: boolean
}

interface CreateOption {
  reason: Extract<AutocompleteChangeReason, 'create-option'>
  option: string
}
interface AnotherOptions {
  reason: Exclude<AutocompleteChangeReason, 'create-option'>
  option: RegionDTO
}

interface HandleRegionsInputDefaultArgs {
  onChange: (value: RegionDTO[]) => void
  oldList: RegionDTO[]
}

type HandleRegionsInputArgs = HandleRegionsInputDefaultArgs &
  (CreateOption | AnotherOptions)

export function FirstStep() {
  const classes = useStyles()
  const navigate = useNavigate()

  const {
    form: {
      control,
      register,
      formState: { errors },
      watch,
    },
    context: { goToSecondStep, isLoadingMutation, isEditProjectPage },
  } = useProject()

  const selectedState = watch('state')

  const { data: states = [] } = useGetAddressStates()
  const { data: cities = [] } = useGetAddressCities(selectedState?.uf)

  const title = isEditProjectPage ? 'Editar' : 'Adicionar'

  function handleBack() {
    navigate(-1)
  }

  function handleNext() {
    goToSecondStep()
  }

  function handleRegionsInput({
    onChange,
    oldList,
    reason,
    option,
  }: HandleRegionsInputArgs) {
    switch (reason) {
      case 'remove-option': {
        let newList

        if (option.id) {
          newList = oldList.map((region) => {
            if (region.name === option.name) return { ...region, removed: true }
            return region
          })
        } else {
          newList = oldList.filter((region) => region.name !== option.name)
        }

        return onChange(newList)
      }
      case 'create-option': {
        const regionExists = oldList.find((item) => item.name === option)

        if (!regionExists) return onChange([...oldList, { name: option }])

        if (regionExists.removed) {
          const newList = oldList.map((region) => {
            if (region.name === regionExists.name)
              return { ...regionExists, removed: false }

            return region
          })

          onChange(newList)
        }
      }
    }
  }

  return (
    <>
      <section className={classes.firstSection}>
        <Typography variant='h1'>{title} nome do projeto e região</Typography>

        <Typography variant='h4'>Informações básicas</Typography>
        <div>
          <InputText
            id='project_name'
            label='Nome do projeto'
            required
            InputProps={{
              endAdornment: (
                <Tooltip
                  title='Os projetos são equivalentes aos centros administrativos, como: usinas, polos e fazendas.'
                  classes={{
                    tooltip: classes.tooltip,
                  }}
                >
                  <HelpOutline />
                </Tooltip>
              ),
            }}
            error={Boolean(errors.project_name)}
            helperText={errors.project_name?.message}
            {...register('project_name')}
          />

          <Controller
            control={control}
            name={'regions'}
            defaultValue={[]}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                className={classes.autocomplete}
                value={value.filter((region) => !region.removed) as any}
                onChange={(_e, _newValue, reason, details) =>
                  handleRegionsInput({
                    onChange,
                    oldList: value,
                    reason,
                    option: details!.option,
                  })
                }
                freeSolo
                options={[]}
                multiple
                disableClearable
                inputTextProps={{
                  label: 'Nome da região',
                  placeholder: 'Pressione "Enter" para adicionar a região',
                  InputProps: {
                    endAdornment: (
                      <Tooltip
                        title='As regiões são os elementos de agrupamento das demandas, podendo ser um conjunto de talhões, um talhão único ou par de parcelas tratamento/controle.'
                        classes={{
                          tooltip: classes.tooltip,
                        }}
                      >
                        <HelpOutline />
                      </Tooltip>
                    ),
                  },
                  error: Boolean(errors.regions),
                  helperText: errors.regions
                    ?.reduce?.((acc, error) => {
                      if (error?.name) acc.push(error?.name?.message)
                      if (error?.geojson?.root)
                        acc.push(error?.geojson?.root?.message)

                      return acc
                    }, [] as Array<string | undefined>)
                    .map((message, index) => (
                      <Fragment key={index}>
                        {message} <br />
                      </Fragment>
                    )),
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      key={option.name}
                      label={
                        <Typography variant='body1'>{option.name}</Typography>
                      }
                      deleteIcon={
                        <IconButton
                          size='small'
                          aria-label={`Remover região ${option.name}`}
                        >
                          <Close />
                        </IconButton>
                      }
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            )}
          />
          <Controller
            control={control}
            name={'state'}
            defaultValue={{ name: '', uf: '' }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                className={classes.autocomplete}
                options={states}
                getOptionLabel={(option: State) =>
                  `${option.name} (${option.uf})`
                }
                value={
                  states.find((state) => state.name === value.name) || null
                }
                onChange={(_e, newValue) => {
                  if (
                    typeof newValue === 'object' &&
                    newValue !== null &&
                    'uf' in newValue
                  ) {
                    onChange({ name: newValue.name, uf: newValue.uf } as State)
                  }
                }}
                inputTextProps={{
                  id: 'state',
                  label: 'Estado',
                  required: true,
                  error: Boolean(errors.state),
                  helperText: errors.state?.message,
                }}
              />
            )}
          />

          <Controller
            control={control}
            name={'city'}
            defaultValue={''}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                className={classes.autocomplete}
                options={cities}
                getOptionLabel={(option: City) => option.name}
                value={cities.find((city) => city.name === value) || null}
                onChange={(_e, newValue) => {
                  if (
                    typeof newValue === 'object' &&
                    newValue !== null &&
                    'name' in newValue
                  ) {
                    onChange(newValue?.name || '')
                  }
                }}
                inputTextProps={{
                  id: 'city',
                  label: 'Cidade',
                  required: true,
                  error: Boolean(errors.city),
                  helperText: errors.city?.message,
                }}
              />
            )}
          />
        </div>
      </section>
      <section className={classes.buttonContainer}>
        <Button labelOnly onClick={handleBack}>
          Cancelar
        </Button>
        <Box display='flex' gridGap='1.3rem'>
          <Button type='submit' outlined loading={isLoadingMutation}>
            Finalizar Cadastro
          </Button>
          <Button onClick={handleNext} disabled={isLoadingMutation}>
            Adicionar polígonos
          </Button>
        </Box>
      </section>
    </>
  )
}
